/**
 * Auth Contracts
 * Zod schemas for authentication API responses.
 * @module api/contracts/auth
 */
import { z } from "zod"

const AccessProfileSchema = z.record(z.any())

const AuthUserSuccessSchema = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.string().nullable(),
  access_profile: AccessProfileSchema,
  permissions_version: z.number(),
  effective_role: z.string().optional(),
})

const LegacyAuthLoginSuccessSchema = z.object({
  token: z.string(),
  id: z.number(),
  role: z.string(),
  access_profile: AccessProfileSchema,
  permissions_version: z.number(),
  effective_role: z.string().optional(),
})

const NestedAuthLoginSuccessSchema = z.object({
  result: z.literal("success"),
  token: z.string().optional(),
  user: AuthUserSuccessSchema,
  message: z.string().optional(),
})

const NestedAuthLoginFailureSchema = z.object({
  result: z.literal("error"),
  user: z.null().optional(),
  message: z.string().optional(),
})

const LegacyAuthLoginFailureSchema = z.union([
  z.object({
    status: z.literal(0),
    message: z.string().optional(),
  }),
  z.object({
    ok: z.literal(0),
    message: z.string().optional(),
  }),
])

// Auth Login Response - supports complete legacy/nested successes and
// explicitly recognized failure shapes.
export const AuthLoginResponseSchema = z.union([
  NestedAuthLoginSuccessSchema,
  LegacyAuthLoginSuccessSchema,
  NestedAuthLoginFailureSchema,
  LegacyAuthLoginFailureSchema,
])

const LegacyTokenCheckCheckedSuccessSchema = z.object({
  checked: z.literal(true),
  id: z.number().optional(),
  role: z.string().optional(),
  access_profile: AccessProfileSchema,
  permissions_version: z.number(),
  effective_role: z.string().optional(),
})

const LegacyTokenCheckCheckedFailureSchema = z.object({
  checked: z.literal(false),
})

const LegacyTokenCheckValidSuccessSchema = z.object({
  valid: z.literal(true),
  id: z.number().optional(),
  role: z.string().optional(),
  access_profile: AccessProfileSchema,
  permissions_version: z.number(),
  effective_role: z.string().optional(),
})

const LegacyTokenCheckValidFailureSchema = z.object({
  valid: z.literal(false),
})

const NestedTokenCheckSuccessSchema = z.object({
  result: z.literal("success"),
  user: AuthUserSuccessSchema,
})

const NestedTokenCheckFailureSchema = z.object({
  result: z.literal("error"),
  user: z.null().optional(),
})

const LegacyTokenCheckFailureSchema = z.union([
  z.object({
    status: z.literal(0),
    message: z.string().optional(),
  }),
  z.object({
    ok: z.literal(0),
    message: z.string().optional(),
  }),
])

export const AuthTokenCheckResponseSchema = z.union([
  NestedTokenCheckSuccessSchema,
  LegacyTokenCheckCheckedSuccessSchema,
  LegacyTokenCheckValidSuccessSchema,
  NestedTokenCheckFailureSchema,
  LegacyTokenCheckCheckedFailureSchema,
  LegacyTokenCheckValidFailureSchema,
  LegacyTokenCheckFailureSchema,
])

// Auth Login Request
export const AuthLoginRequestSchema = z.object({
  action: z.literal("login").optional(),
  email: z.string().email(),
  password: z.string().min(1),
})

// CSRF Token Response
export const CsrfTokenResponseSchema = z.object({
  token: z.string(),
})

// Type exports
export const AuthTokenCheckResponse = AuthTokenCheckResponseSchema
export const AuthLoginRequest = AuthLoginRequestSchema
export const AuthLoginResponse = AuthLoginResponseSchema
export const CsrfTokenResponse = CsrfTokenResponseSchema
